import { persistCombineReducers, persistReducer } from 'redux-persist';
import autoMergeLevel1 from 'redux-persist/lib/stateReconciler/autoMergeLevel1';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web

import expireIn from 'redux-persist-transform-expire-in';
import { CookieStorage } from 'redux-persist-cookie-storage';
import Cookies from 'cookies-js';
import {
  createOmittingStateReconciler,
  omitNestedPersistedFields,
} from './PersistenceSanitizer';
import main from './ducks/legacy/MainDuck';
import authorization from './ducks/legacy/AuthorizationDuck';
import order, { setOrderTime, setOrderType } from './ducks/legacy/OrderDuck';
import profile from './ducks/legacy/ProfileDuck';
import restaurants from './ducks/legacy/RestaurantsDuck';
import stripe from './ducks/legacy/StripeDuck';
import time, { setSelectedTime } from './ducks/new/Time/TimeReducer';
import restaurant from './ducks/new/Restaurant/RestaurantReducer';
import address from './ducks/new/Address/AddressReducer';
import seo from './ducks/new/Seo/SeoReducer';
import gift from './ducks/legacy/GiftDuck';
import reorder from './ducks/legacy/ReorderDuck';
import { isConsumerApp } from '../../../util/Helpers';
import { ORDER_TYPE_OPTIONS } from '../../../util/GetOrderFilterOptions';
import {
  resolveListingOrderTime,
  restoreOrderTime,
  sameOrderTime,
} from '../../../util/listingOrderTime';

const safeDeserialize = (serial) => {
  if (serial == null || serial === 'undefined' || serial === '') {
    return undefined;
  }
  try {
    return JSON.parse(serial);
  } catch (e) {
    return undefined;
  }
};

const logoutActionName = 'authorization/logoutResponse';

// https://github.com/sirLisko/redux-persist-transform-expire-in
const expirationDelay = 1 * 60 * 60 * 1000; // expire in 60 minutes
const expirationKey = 'expirationKey';
const appVersion = process.env?.REACT_APP_VERSION ?? '0.0.0';

const persistConfig = {
  key: `rootstore_${appVersion}`,
  storage,
  transforms: [
    expireIn(expirationDelay, expirationKey, []),
    omitNestedPersistedFields('order', [
      'menuSearchText',
      'menuSearchRevision',
      'menuSearchContext',
      'cartAttemptId',
      'checkoutStartResult',
    ]),
  ],
  blacklist: ['profile', 'main', 'restaurants'],
  deserialize: safeDeserialize,
  stateReconciler(inbound, original, reduced, config) {
    const merged = autoMergeLevel2(inbound, original, reduced, config);
    if (!isConsumerApp() || !inbound?.time) return merged;
    const useNested =
      reduced.time?._persist?.rehydrated && // eslint-disable-line no-underscore-dangle
      restoreOrderTime(reduced.time.selectedTime) != null;
    // The root also persisted the nested slice. Its saved _persist marker
    // must not pretend that the independent timeStore read has completed.
    return {
      ...merged,
      time: {
        ...reduced.time,
        ...(useNested ? {} : inbound.time),
        _persist: reduced.time._persist, // eslint-disable-line no-underscore-dangle
        selectedTime: restoreOrderTime(
          useNested ? reduced.time.selectedTime : inbound.time.selectedTime,
          useNested ? inbound.time.selectedTime : reduced.time.selectedTime
        ),
        selectedDate: useNested
          ? reduced.time.selectedDate
          : inbound.time.selectedDate,
      },
    };
  },
};

const mainPersistConfig = {
  key: `mainStore_${appVersion}`,
  storage,
  blacklist: ['globalLoaderQueue', 'searchValue', 'searchRevision'],
  stateReconciler: createOmittingStateReconciler(autoMergeLevel1, [
    'searchValue',
    'searchRevision',
  ]),
  deserialize: safeDeserialize,
};

const profilePersistConfig = {
  key: `profileStore_${appVersion}`,
  storage,
  blacklist: ['getAddressesResponse', 'resetPasswordResponse'],
  deserialize: safeDeserialize,
};

const restaurantsPersistConfig = {
  key: `restaurantsStore_${appVersion}`,
  storage,
  whitelist: ['selectedRestaurant'],
  deserialize: safeDeserialize,
};

const authPersistConfig = {
  key: `authStore_${appVersion}`,
  storage: new CookieStorage(Cookies),
  stateReconciler(inboundState, originalState) {
    return originalState;
  },
  blacklist: ['addresses'],
  deserialize: safeDeserialize,
};

const timePersistConfig = {
  key: `timeStore_${appVersion}`,
  storage,
  transforms: [expireIn(expirationDelay, 'timeExpirationKey', [])],
  blacklist: [],
  deserialize: safeDeserialize,
  stateReconciler(inbound, original, reduced, config) {
    const merged = autoMergeLevel1(inbound, original, reduced, config);
    if (!isConsumerApp()) return merged;
    return {
      ...merged,
      ...(restoreOrderTime(inbound?.selectedTime) == null ? reduced : {}),
      selectedTime: restoreOrderTime(
        inbound?.selectedTime,
        reduced.selectedTime
      ),
    };
  },
};

const restaurantNewPersistConfig = {
  key: `restaurantNewStore_${appVersion}`,
  storage,
  blacklist: [],
  deserialize: safeDeserialize,
};

const addressPersistConfig = {
  key: `addressStore_${appVersion}`,
  storage,
  blacklist: [],
  deserialize: safeDeserialize,
};

const seoPersistConfig = {
  key: `seoStore_${appVersion}`,
  storage,
  blacklist: [],
  deserialize: safeDeserialize,
};

const reducers = {
  // legacy
  order,
  stripe,
  gift,
  reorder,
  restaurants: persistReducer(restaurantsPersistConfig, restaurants),
  authorization: persistReducer(authPersistConfig, authorization),
  main: persistReducer(mainPersistConfig, main),
  profile: persistReducer(profilePersistConfig, profile),
  // new
  time: persistReducer(timePersistConfig, time),
  restaurant: persistReducer(restaurantNewPersistConfig, restaurant),
  address: persistReducer(addressPersistConfig, address),
  seo: persistReducer(seoPersistConfig, seo),
};

export const rootReducer = persistCombineReducers(persistConfig, reducers);

const appReducer = (state, action) => {
  if (action.type === logoutActionName) {
    return rootReducer(undefined, action);
  }
  let next = rootReducer(state, action);
  // Both copies of persisted time must finish restoring before selecting a
  // default. PersistGate mounts consumers only after this boundary. Keeping
  // ownership here also works without a header or a particular route mounting.
  if (
    isConsumerApp() &&
    next._persist?.rehydrated && // eslint-disable-line no-underscore-dangle
    next.time?._persist?.rehydrated // eslint-disable-line no-underscore-dangle
  ) {
    if (!['delivery', 'takeout'].includes(next.order.type?.value))
      next = rootReducer(next, setOrderType(ORDER_TYPE_OPTIONS[1]));
    const effective = resolveListingOrderTime(
      next.time.selectedTime,
      next.order.time
    );
    if (effective !== next.time.selectedTime)
      next = rootReducer(next, setSelectedTime(effective));
    if (
      effective !== next.order.time &&
      !sameOrderTime(effective, next.order.time)
    )
      next = rootReducer(next, setOrderTime(effective));
  }
  return next;
};

export default appReducer;
